import { useReactable } from "@reactables/react";
import { Field } from "@reactables/react-forms";
import { Modal } from "react-bootstrap";
import ExcludedActivitiesList from "../ExcludedActivitiesList/ExcludedActivitiesList";
import { Selectors, RxToggle } from "@jauntin/reactables";
import { TwoStageButton, CheckboxInput } from "@jauntin/react-ui";
import { ExtendedMeta } from "@jauntin/utilities";
import CurrencyHelper from "Helpers/CurrencyHelper";
import DollarsAndCents from "Features/Shared/Components/DollarsAndCents";
import { RxCoverageApplicationProp } from "../../Rx/RxCoverageApplication/RxCoverageApplication";
import * as PurchaseSelectors from "../../Rx/RxCoverageApplication/Selectors/purchase.selector";
import * as ConfirmationSelectors from "../../Rx/RxCoverageApplication/Selectors/confirmation.selector";
import * as FormSelectors from "../../Rx/RxCoverageApplication/Selectors/form.selector";
import { getReferralQueryString } from "Features/CoverageApplication/Rx/RxCoverageApplication/Selectors/referral.selector";
import { Token } from "Features/CoverageApplication/Models/applicationForm.model";

const btnText = (isLoading) => (
  <>
    {isLoading && (
      <span
        className="spinner-border spinner-border-sm text-light align-middle"
        role="status"
        aria-hidden="true"
      />
    )}
    <span className="mx-2">Purchase</span>
  </>
);
const Purchase = ({
  rxCoverageApplication,
}: {
  rxCoverageApplication: RxCoverageApplicationProp;
}) => {
  const [state, actions] = rxCoverageApplication;

  const [showExcludedActivitiesModal, excludedActivitiesModalActions] =
    useReactable(RxToggle);

  const { priceValues } = state;
  const isLoading = state.purchase.loading;
  const checkoutPageConfirmed =
    ConfirmationSelectors.getCheckoutPageConfirmed(state);

  const showErrors = ({ touched, error }: ExtendedMeta) => {
    return (touched || state.touchButtons.purchase) && error;
  };

  const referralParams = getReferralQueryString(state);

  return (
    <>
      <div className="form-row mb-2 pt-2 mt-2 border-top">
        <div className="col font-weight-bold">Total</div>
        <div className="col-auto font-weight-bold">
          <DollarsAndCents
            currency={new CurrencyHelper()
              .setValue(priceValues.total)
              .convertFromInt()}
          />
        </div>
      </div>
      {checkoutPageConfirmed && (
        <div className="pt-3 mt-3">
          <Field
            name="purchase.excludedActivities"
            labelClassName="font-italic"
            label={
              <>
                I have reviewed the list of{" "}
                <span
                  tabIndex={0}
                  role="button"
                  className="font-italic span-link span-link--normal"
                  onClick={excludedActivitiesModalActions.toggleOn}
                  onKeyPress={(e) => {
                    if (
                      e.key === "Enter" ||
                      e.key === " " ||
                      e.key === "Spacebar"
                    ) {
                      excludedActivitiesModalActions.toggleOn();
                    }
                  }}
                >
                  excluded objects and activities
                </span>{" "}
                and understand they are not covered by the policy.
              </>
            }
            component={CheckboxInput}
            showErrors={showErrors}
          />
          <Field
            component={CheckboxInput}
            labelClassName="font-italic"
            label={
              <>
                I agree that the entered information is correct to the best of
                my knowledge, and I have reviewed the{" "}
                <a
                  href={`${process.env.REACT_APP_FRAUD_WARNING_LINK}${referralParams}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="span-link span-link--normal"
                >
                  fraud warnings
                </a>
                , and&nbsp;
                <a
                  href={`${process.env.REACT_APP_MARKETING_SITE_HOST}refunds-and-cancellations${referralParams}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="span-link span-link--normal"
                >
                  refund policy
                </a>
                .
              </>
            }
            name="purchase.consent"
            showErrors={showErrors}
          />
          {state.purchase.error && (
            <div className="purchase-error">
              Your request could not be processed at this time. Please confirm
              billing address and/or contact your payment card financial
              institution.
            </div>
          )}
          {(() => {
            const hasVisibleError = Selectors.hasControlTouchError(
              state.applicationForm,
              "purchase",
            );

            const buttonTouched = state.touchButtons.purchase;
            const formValid = FormSelectors.getControl(state).valid;
            const actionEnabled = buttonTouched || formValid;
            const disabled = hasVisibleError || (actionEnabled && !formValid);

            return (
              <TwoStageButton
                disabled={disabled}
                actionEnabled={actionEnabled}
                action={() => {
                  actions.createCoverage({
                    payload: PurchaseSelectors.getCreateCoveragePayload(state),
                    tokenExpiry: (
                      state.applicationForm.root.value.payment.token as Token
                    ).expiry,
                  });
                }}
                touch={() => actions.touchButton("purchase")}
                text={btnText(isLoading)}
                className="btn btn-primary d-block mt-3 w-100"
              />
            );
          })()}
        </div>
      )}
      <Modal
        show={showExcludedActivitiesModal}
        onHide={excludedActivitiesModalActions.toggleOff}
        dialogClassName="modal--width-lg px-md-5"
      >
        <Modal.Header closeButton>
          <h2 className="modal-title h3">Excluded objects & activities</h2>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            The following activities or use of the following objects are not
            covered by the policy.
          </p>
          <ExcludedActivitiesList
            excludedActivities={state.excludedActivities.data}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Purchase;
